import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * App-wide hover tooltip — ONE styled chip for every element in the product, so
 * an icon button, a badge, a status pill and an avatar all read identically on
 * hover (the same chip the Space assignee stack + Avatar use).
 *
 * How it works (zero per-call-site changes): a single delegated `pointerover`
 * listener finds the nearest ancestor carrying a `title` (or `data-tooltip`)
 * attribute. The native `title` is MOVED to `data-tooltip` the first time an
 * element is hovered — this suppresses the browser's slow, unstyled OS tooltip
 * so ours is the only one shown. Screen-reader access is preserved by copying
 * the text to `aria-label` when the element has no accessible name yet.
 *
 * Avatars render their OWN identical chip (Avatar.jsx) and never carry a
 * `title`, so they're simply left alone here — no double tooltip.
 *
 * Positioning mirrors Avatar's chip: centred above the target, auto-flipping
 * below when the target sits near the viewport top, clamped to the viewport,
 * rendered through a portal so no ancestor `overflow:hidden` can clip it.
 */
export function GlobalTooltip() {
  const [state, setState] = useState(null) // { text, top, anchorX, flipBelow }
  const [placed, setPlaced] = useState(null) // { left, caretOffset } after measuring
  const targetRef = useRef(null)
  const chipRef = useRef(null)

  const hide = useCallback(() => {
    targetRef.current = null
    setState(null)
    setPlaced(null)
  }, [])

  const showFor = useCallback((el) => {
    // Resolve the tooltip text. A LIVE `title` is always the freshest value —
    // React re-adds it whenever the bound label changes (e.g. the dark-mode
    // toggle flipping "Switch to dark/light mode"), so prefer it: refresh
    // data-tooltip + strip the native title. Otherwise (title already moved, or
    // an element that set data-tooltip directly) use the stored data-tooltip.
    //
    // Reading data-tooltip first here was the two-tooltip bug: after a title
    // changed, the stale data-tooltip won and the newly re-added native title
    // was never removed — so the browser's OS tooltip showed ALONGSIDE our chip.
    let text = el.getAttribute('title')
    if (text != null) {
      el.setAttribute('data-tooltip', text)
      el.removeAttribute('title')
      // Preserve the accessible name the title used to provide.
      if (!el.getAttribute('aria-label') && !el.textContent.trim()) {
        el.setAttribute('aria-label', text)
      }
    } else {
      text = el.getAttribute('data-tooltip')
      if (text == null) return
    }
    text = String(text).trim()
    if (!text) return

    const r = el.getBoundingClientRect()
    if (r.width === 0 && r.height === 0) return
    const ESTIMATED_H = 36
    const PADDING = 6
    const flipBelow = r.top < ESTIMATED_H + PADDING
    targetRef.current = el
    // Store the raw target-centre X; the actual chip left/caret are resolved
    // after render in the layout effect once we can measure the wrapped width.
    setPlaced(null)
    setState({
      text,
      top: flipBelow ? r.bottom + PADDING : r.top - PADDING,
      anchorX: r.left + r.width / 2,
      flipBelow,
    })
  }, [])

  // After the chip first renders at left:0 (full width available → it wraps to
  // its natural width, not squished by the viewport edge) we measure it, then
  // pin its LEFT edge explicitly and clamp the whole box inside the viewport.
  // No translateX(-50%) — that would let the edge re-squish a width:auto box.
  // Runs before paint (useLayoutEffect) → no visible jump.
  useLayoutEffect(() => {
    if (!state || !chipRef.current) return
    const MARGIN = 8
    const w = chipRef.current.offsetWidth
    const vw = window.innerWidth
    // Centre the box on the target, then clamp both edges into the viewport.
    let left = state.anchorX - w / 2
    left = Math.min(Math.max(left, MARGIN), Math.max(MARGIN, vw - MARGIN - w))
    // Caret points at the target centre; keep it within the chip's edges.
    const caretLeft = Math.max(8, Math.min(w - 8, state.anchorX - left))
    setPlaced({ left, caretLeft })
  }, [state])

  useEffect(() => {
    const onOver = (e) => {
      // Touch has NO hover state — a tap fires `pointerover`, which would show
      // this hover chip and leave it stuck (no `pointerout` follows a tap). A
      // tooltip on tap is never wanted (the tap navigates / acts), so ignore
      // touch pointers entirely; also dismiss any chip a prior mouse left up.
      if (e.pointerType === 'touch') {
        if (targetRef.current) hide()
        return
      }
      const el =
        e.target instanceof Element
          ? e.target.closest('[data-tooltip], [title]')
          : null
      if (!el) {
        if (targetRef.current) hide()
        return
      }
      if (el === targetRef.current) return
      showFor(el)
    }
    const onOut = (e) => {
      // Hide once the pointer truly leaves the active target (not when moving
      // onto a child — `contains` guards the child case).
      const to = e.relatedTarget
      if (
        targetRef.current &&
        (!(to instanceof Node) || !targetRef.current.contains(to))
      ) {
        hide()
      }
    }
    const onScroll = () => targetRef.current && hide()

    document.addEventListener('pointerover', onOver, true)
    document.addEventListener('pointerout', onOut, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    // Any click (e.g. opening a menu the tooltip described) dismisses it.
    document.addEventListener('pointerdown', onScroll, true)
    // Touch devices: a tap fires `pointerover` (showing the chip) but never a
    // reliable `pointerout`, and momentum touch-scroll may not surface a
    // `scroll` event the window-capture listener catches — so the chip could
    // linger. `touchmove` fires the moment a finger drags to scroll, so hide on
    // it too. Passive: we only read state, never preventDefault.
    document.addEventListener('touchmove', onScroll, { capture: true, passive: true })
    return () => {
      document.removeEventListener('pointerover', onOver, true)
      document.removeEventListener('pointerout', onOut, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      document.removeEventListener('pointerdown', onScroll, true)
      document.removeEventListener('touchmove', onScroll, { capture: true })
    }
  }, [hide, showFor])

  if (!state) return null
  // First render at left:0 (full width available) so the layout effect can
  // measure the chip's natural wrapped width; it's kept invisible until placed.
  const left = placed ? placed.left : 0
  return createPortal(
    <span
      ref={chipRef}
      role="tooltip"
      style={{
        position: 'fixed',
        top: state.top,
        left,
        transform: state.flipBelow ? 'translateY(0)' : 'translateY(-100%)',
        zIndex: 1000,
        maxWidth: 'min(20rem, calc(100vw - 16px))',
        visibility: placed ? 'visible' : 'hidden',
      }}
      className="pointer-events-none whitespace-normal break-words [overflow-wrap:anywhere] text-center rounded-md bg-[color:var(--color-ink-strong)] text-[color:var(--color-canvas)] text-[12px] font-medium px-2 py-1 shadow-md"
    >
      {state.text}
      <span
        aria-hidden="true"
        style={{ left: placed ? `${placed.caretLeft}px` : '50%' }}
        className={
          state.flipBelow
            ? 'absolute bottom-full -translate-x-1/2 h-0 w-0 border-x-4 border-x-transparent border-b-4 border-b-[color:var(--color-ink-strong)]'
            : 'absolute top-full -translate-x-1/2 h-0 w-0 border-x-4 border-x-transparent border-t-4 border-t-[color:var(--color-ink-strong)]'
        }
      />
    </span>,
    document.body,
  )
}
